import type {
  ArticleDto,
  InPriceValuesDto,
  OutPriceConfigDto,
  OutPriceValueDto,
  PriceDto,
  PriceRangeDto,
  SkuDto,
} from '@/ApiClient.ts';
import { isChargeInPriceStepValueDto, isPercentageInPriceStepValueDto, MarkupMarginType } from '@/ApiClient.ts';
import { assertUnreachable, whereNotNull } from '@/lib/type-helpers';
import { useActiveSeason } from '@/store/active-season.ts';
import { useCurrencies } from '@/store/currencies';
import { useDestinations } from '@/store/destinations';
import { useSelectedDestination } from '@/store/selected-destination';
import { useMarkupTargets } from '@/store/markup-targets';
import { useInPriceConfigs } from '@/store/in-price-configs.ts';
import { useOutPriceConfigs } from '@/store/out-price-configs.ts';
import { useSeasons } from '@/store/seasons';
import { useSizeSystems } from '@/store/size-systems';
import isNil from 'lodash/isNil';
import uniq from 'lodash/uniq';
import { formatNumber } from '@/components/prices/utils';
import { calculateMarkupMargin } from '@/components/prices/utils/price-math';
import { findOutPriceValuesForArticleSeason } from '@/components/prices/utils/helpers';

export type PriceGroup = {
  supplierId: number;
  inPrice: InPriceValuesDto | undefined;
  outPrices: OutPriceValueDto[];
  articles: ArticleDto[];
  skus: SkuDto[];
};

export type SeasonPriceGroups = Record<number, PriceGroup[]>;
export type SeasonPriceGroupsMap = Map<number, PriceGroup[]>;

export const usePricingGroups = () => {
  const addToEmptyGroup = (
    priceGroupMap: Map<string, PriceGroup>,
    outPriceConfig: OutPriceConfigDto | undefined,
    article: ArticleDto,
    sku?: SkuDto
  ) => {
    const key = 'empty';

    if (!priceGroupMap.has(key)) {
      const emptyOutPrices = findOutPriceValuesForArticleSeason(outPriceConfig, undefined, article.hierarchyNodeId);
      priceGroupMap.set(key, {
        supplierId: -1,
        inPrice: undefined,
        outPrices: emptyOutPrices,
        articles: [],
        skus: [],
      });
    }

    const group = priceGroupMap.get(key)!;
    if (sku) {
      group.skus.push(sku);
    }
    if (!group.articles.includes(article)) {
      group.articles.push(article);
    }
  };

  /**
   * Aggregates SKUs into price groups per season based on their in-prices and out-prices. Used for the price popper on a product level.
   * Groups by individual SKU prices - an article can appear in multiple groups if its SKUs have different prices, but a SKU can only be in one group.
   * The structure is: Record<seasonId, PriceGroup[]> where PriceGroup contains supplierId, inPrice, outPrices, the list of articles and the SKUs that share these prices.
   * Sorted by season end date (furthest into the future first), and within each season, groups are sorted by:
   * 1. Groups that have the season (non-empty groups with actual supplier/price data) come first.
   * 2. Within those, groups are sorted by their first (smallest) size in size-system range order.
   * 3. SKU count (most SKUs first) breaks ties between groups sharing a first size.
   * 4. Empty groups (no supplier/price data) come last.
   */
  const getGroupedSeasonalInPricesForArticles = (articles: ArticleDto[], supplierId?: number): SeasonPriceGroups => {
    const seasonGroupMap = new Map<number, Map<string, PriceGroup>>();

    // First pass: collect all unique season IDs from SKU-level seasonalities
    const allSeasonIds = new Set<number>();
    const { activeSeason } = useActiveSeason();

    if (activeSeason?.id) {
      allSeasonIds.add(activeSeason.id);
    }

    if (allSeasonIds.size === 0) {
      allSeasonIds.add(-1);
    }

    articles.forEach((article) => {
      article.skus?.forEach((sku) => {
        sku.seasonalities?.forEach((seasonality) => {
          if (seasonality.id) {
            allSeasonIds.add(seasonality.id);
          }
        });
      });
    });

    // Second pass: process each SKU for every season
    allSeasonIds.forEach((seasonId) => {
      if (!seasonGroupMap.has(seasonId)) {
        seasonGroupMap.set(seasonId, new Map<string, PriceGroup>());
      }

      const outPriceConfig = useOutPriceConfigs().getBySeasonId(seasonId);
      const priceGroupMap = seasonGroupMap.get(seasonId)!;

      // Tariff code only influences price through the duty lookup, which only runs when the season has its
      // own in-price config with duties enabled. This matches how the popper resolves config — getBySeasonId
      // does NOT fall back to the "No season" config for a real season, so a season without a config has no
      // duties. Without a duty calc the tariff changes nothing, so keep it out of the grouping key — else
      // SKUs split on a code with no price effect.
      const seasonInPriceConfig = useInPriceConfigs().getBySeasonId(seasonId === -1 ? null : seasonId);
      const tariffAffectsPrice = seasonInPriceConfig?.useDuties === true;

      articles.forEach((article) => {
        const skus = article.skus ?? [];

        if (skus.length === 0) {
          addToEmptyGroup(priceGroupMap, outPriceConfig, article);
          return;
        }

        skus.forEach((sku) => {
          const skuSeason =
            seasonId === -1 ? sku.seasonalities?.find((s) => !s.id) : sku.seasonalities?.find((s) => s.id === seasonId);

          if (skuSeason) {
            const matchedSupplier =
              supplierId !== undefined
                ? skuSeason.suppliers?.find((s) => s.supplierId === supplierId)
                : skuSeason.suppliers?.[0];

            // Groups follow the selected destination's prices — other destinations' rows don't affect grouping
            const inPrice = matchedSupplier?.inPriceValues.find(
              (v) => v.destinationId === useSelectedDestination().selectedDestination?.id
            );
            const hasInPriceData =
              inPrice &&
              (inPrice.costPrice?.amount != null ||
                inPrice.landedCost?.amount != null ||
                inPrice.costPriceTarget?.amount != null);

            const outPrices = findOutPriceValuesForArticleSeason(outPriceConfig, skuSeason, article.hierarchyNodeId);
            const hasOutPriceData = outPrices.some((op) => !isNil(op.amount));

            if ((!matchedSupplier || !hasInPriceData) && !hasOutPriceData) {
              addToEmptyGroup(priceGroupMap, outPriceConfig, article, sku);
              return;
            }

            const matchedSupplierId = matchedSupplier?.supplierId ?? -1;
            // An out-price with no amount shows no price in the popper (getPriceRangesFromGroups skips
            // amount-less out-prices), so it must not affect grouping. Its markup/margin target is latent
            // config that can legitimately differ between otherwise-identical SKUs — e.g. one SKU carrying a
            // stored target while a newly-added size falls back to the hierarchy default — which split them
            // into separate groups over a price the user can't see.
            const outPricesKey = outPrices
              .filter((op) => !isNil(op.amount))
              .map(
                (op) =>
                  `${op.outPriceId}-${op.amount}-${op.currencyId}-${formatNumber(op.markupMarginTarget)}-${op.markupMarginType}`
              )
              .join('|');
            const stepValuesKey =
              inPrice?.stepValues
                ?.map((sv) => {
                  if (isPercentageInPriceStepValueDto(sv)) {
                    return `${sv.inPriceStepId}-pct-${sv.percentage}`;
                  }
                  if (isChargeInPriceStepValueDto(sv)) {
                    return `${sv.inPriceStepId}-chg-${sv.amount}-${sv.currencyId}`;
                  }
                  assertUnreachable(sv);
                })
                .join('|') ?? '';

            const key = `${matchedSupplierId}-${inPrice?.costPrice?.amount}-${tariffAffectsPrice ? article.tariffCodeIds?.[0] : ''}-${inPrice?.costPrice?.currencyId}-${inPrice?.costPriceTarget?.amount}-${inPrice?.costPriceTarget?.currencyId}-${inPrice?.landedCost?.amount}-${inPrice?.landedCost?.currencyId}-${inPrice?.freightPercentage}-${inPrice?.freightCharge?.amount}-${inPrice?.freightCharge?.currencyId}-${inPrice?.dutiesPercentage}-${stepValuesKey}-${outPricesKey}`;

            if (!priceGroupMap.has(key)) {
              priceGroupMap.set(key, {
                supplierId: matchedSupplierId,
                inPrice,
                outPrices,
                articles: [],
                skus: [],
              });
            }

            const group = priceGroupMap.get(key)!;
            group.skus.push(sku);
            if (!group.articles.includes(article)) {
              group.articles.push(article);
            }
          } else {
            addToEmptyGroup(priceGroupMap, outPriceConfig, article, sku);
          }
        });
      });
    });

    // Convert to array to sort seasons by end date
    const seasons = useSeasons();
    const sizeSystems = useSizeSystems();

    // The group's first (smallest) size as an index into the size-system range, so groups order
    // 42 before 43-49 rather than by SKU count. Unknown sizes / no version sort last. Cached per
    // version and per group — the sort comparator asks repeatedly, and range scans are linear.
    const sizeIndexByVersion = new Map<number, Map<number, number>>();
    const sizeIndicesFor = (versionId: number): Map<number, number> => {
      let indices = sizeIndexByVersion.get(versionId);
      if (!indices) {
        indices = new Map(sizeSystems.getSizeSystemVersionFromId(versionId)?.range?.map((s, i) => [s.id, i]) ?? []);
        sizeIndexByVersion.set(versionId, indices);
      }
      return indices;
    };
    const firstSizeIndexCache = new Map<PriceGroup, number>();
    const firstSizeIndexOf = (group: PriceGroup): number => {
      const cached = firstSizeIndexCache.get(group);
      if (cached !== undefined) return cached;

      const versionId = group.articles[0]?.product?.sizeSystemVersionId;
      const indices = versionId ? sizeIndicesFor(versionId) : undefined;
      let min = Number.MAX_SAFE_INTEGER;
      if (indices?.size)
        for (const sku of group.skus) {
          const idx = indices.get(sku.size.sizeId);
          if (idx !== undefined && idx < min) min = idx;
        }
      firstSizeIndexCache.set(group, min);
      return min;
    };

    const seasonEntries = Array.from(seasonGroupMap.entries())
      .map(([seasonId, priceGroupMap]) => {
        const season = seasons.byId[seasonId];
        const sortedGroups = Array.from(priceGroupMap.values()).sort((a, b) => {
          const aHaveSeason = a.supplierId !== -1;
          const bHaveSeason = b.supplierId !== -1;

          if (aHaveSeason && !bHaveSeason) return -1;
          if (!aHaveSeason && bHaveSeason) return 1;

          const aIsEmpty =
            a.supplierId === -1 && a.inPrice === undefined && a.outPrices.every((op) => op.amount === undefined);
          const bIsEmpty =
            b.supplierId === -1 && b.inPrice === undefined && b.outPrices.every((op) => op.amount === undefined);

          if (aIsEmpty && !bIsEmpty) return 1;
          if (!aIsEmpty && bIsEmpty) return -1;

          const aIndex = firstSizeIndexOf(a);
          const bIndex = firstSizeIndexOf(b);
          if (aIndex !== bIndex) return aIndex - bIndex;

          return b.skus.length - a.skus.length;
        });

        return {
          seasonId,
          order: season?.order ?? Number.MAX_SAFE_INTEGER,
          groups: sortedGroups,
        };
      })
      .sort((a, b) => a.order - b.order);

    const result: SeasonPriceGroups = {};
    seasonEntries.forEach(({ seasonId, groups }) => {
      result[seasonId] = groups;
    });

    return result;
  };

  const getPriceRange = (
    prices: (Partial<PriceDto> | undefined)[],
    name?: string
  ): { min?: number; max?: number; currencyIds: number[]; name?: string } => {
    const amounts = prices.map((price) => price?.amount).filter(whereNotNull);
    const currencyIds = uniq(prices.map((price) => price?.currencyId).filter(whereNotNull));

    return {
      min: amounts.length > 0 ? Math.min(...amounts) : undefined,
      max: amounts.length > 0 ? Math.max(...amounts) : undefined,
      currencyIds,
      name,
    };
  };

  const getPriceRangesFromGroups = (
    groups: PriceGroup[],
    options?: {
      seasonId?: number;
      preferMarkup?: boolean;
      outPriceFilter?: (op: OutPriceValueDto) => boolean;
    }
  ) => {
    const { seasonId, preferMarkup = true, outPriceFilter } = options ?? {};

    const costPriceRange = getPriceRange(
      groups.map((g) => g.inPrice?.costPrice),
      'Cost Price'
    );

    const numOutPrices = groups[0]?.outPrices.length ?? 0;
    const outPriceRanges = [];
    for (let i = 0; i < numOutPrices; i++) {
      let prices = groups.map((g) => g.outPrices[i]).filter(whereNotNull);
      if (outPriceFilter) prices = prices.filter(outPriceFilter);
      if (prices.length === 0 || prices.every((p) => isNil(p.amount))) continue;

      // Markup / margin (per the user's preference) for this out-price, shown as
      // a pill beside the value.
      let markupMarginRange: (ReturnType<typeof getPriceRange> & { trend?: number }) | undefined;
      if (seasonId !== undefined) {
        // How the figure stands against its target, the popper's own rule: (value − target) / target.
        // One trend for a range of groups is the worst of them — a group under target is the thing
        // conditional colour exists to flag, so it is never averaged away by the ones above it.
        let trend: number | undefined;
        const markups = groups
          .flatMap((g) => {
            const op = g.outPrices[i];
            if (!op || (outPriceFilter && !outPriceFilter(op))) return [];
            const landedCost = g.inPrice?.landedCost;
            const markup = calculateMarkupMargin(
              op.amount,
              landedCost?.amount,
              op.markupMarginType,
              op.currencyId,
              landedCost?.currencyId,
              seasonId
            );
            if (isNil(markup)) return [];
            if (op.markupMarginTarget) {
              const delta = (markup - op.markupMarginTarget) / op.markupMarginTarget;
              trend = trend === undefined ? delta : Math.min(trend, delta);
            }
            const markupMargin = useMarkupTargets().getMarkupMarginAs(
              { amount: markup, type: op.markupMarginType },
              preferMarkup ? MarkupMarginType.Markup : MarkupMarginType.Margin
            );
            return [{ amount: formatNumber(markupMargin) }];
          })
          .filter(whereNotNull);
        if (markups.length) {
          markupMarginRange = { ...getPriceRange(markups, preferMarkup ? 'Markup' : 'Margin'), trend };
        }
      }

      outPriceRanges.push({ ...getPriceRange(prices, prices[0]?.outPriceName), markupMarginRange });
    }

    let markupMarginRange: ReturnType<typeof getPriceRange> | undefined;
    if (outPriceRanges.length <= 1 && seasonId !== undefined) {
      const markups = groups
        .flatMap((p) => {
          let outPrices = p.outPrices;
          if (outPriceFilter) outPrices = outPrices.filter(outPriceFilter);
          return outPrices.flatMap((op) => {
            const landedCost = p.inPrice?.landedCost;
            const markup = calculateMarkupMargin(
              op.amount,
              landedCost?.amount,
              op.markupMarginType,
              op.currencyId,
              landedCost?.currencyId,
              seasonId
            );

            if (!isNil(markup)) {
              const markupMargin = useMarkupTargets().getMarkupMarginAs(
                { amount: markup, type: op.markupMarginType },
                preferMarkup ? MarkupMarginType.Markup : MarkupMarginType.Margin
              );
              return { amount: formatNumber(markupMargin) };
            }
          });
        })
        .filter(whereNotNull);

      markupMarginRange = getPriceRange(markups, preferMarkup ? 'Markup' : 'Margin');
    }

    return { costPriceRange, outPriceRanges, markupMarginRange };
  };

  const formatPriceRange = (min: number | undefined, max: number | undefined, currencyId?: number, unit?: string) => {
    if (isNil(min) && isNil(max)) {
      return '-';
    }

    if (unit) {
      return min === max ? `${min}${unit}` : `${min} - ${max}${unit}`;
    }

    if (typeof currencyId !== 'number') {
      return min === max ? `${min}` : `${min} - ${max}`;
    }

    const currencies = useCurrencies();
    if (min === max) {
      return currencies.formatPrice(min!, currencyId)!;
    }

    return `${currencies.formatPrice(min!, currencyId)} - ${currencies.formatPrice(max!, currencyId)}`;
  };

  return { getGroupedSeasonalInPricesForArticles, getPriceRangesFromGroups, getPriceRange, formatPriceRange };
};

export function getMainOutPriceRangeForArticle(article: ArticleDto): PriceRangeDto | undefined {
  const activeSeason = useActiveSeason();
  const outPriceConfigs = useOutPriceConfigs();
  const seasons = useSeasons();

  // Match server logic: when no active season, pick the first non-base season by order
  let targetSeasonId = activeSeason.activeSeasonId;
  if (targetSeasonId == null) {
    const nonBaseSeasonIds = (article.seasonalities ?? []).filter((s) => s.id != null).map((s) => s.id!);
    targetSeasonId = nonBaseSeasonIds.sort(
      (a, b) => (seasons.byId[a]?.order ?? Infinity) - (seasons.byId[b]?.order ?? Infinity)
    )[0];
  }

  const skuPrices: { amount: number; currencyId: number }[] = [];

  for (const sku of article.skus ?? []) {
    const seasonality =
      targetSeasonId != null
        ? (sku.seasonalities ?? []).find((s) => s.id === targetSeasonId)
        : (sku.seasonalities ?? [])[0];
    if (!seasonality) continue;

    const mainOutPriceValue = outPriceConfigs.getMainOutPriceValue(seasonality.outPriceValues ?? [], seasonality.id);
    if (mainOutPriceValue?.amount != null && mainOutPriceValue?.currencyId != null) {
      skuPrices.push({ amount: mainOutPriceValue.amount, currencyId: mainOutPriceValue.currencyId });
    }
  }

  if (skuPrices.length === 0) return undefined;

  const amounts = skuPrices.map((p) => p.amount);
  return {
    minAmount: Math.min(...amounts),
    maxAmount: Math.max(...amounts),
    currencyId: skuPrices[0].currencyId,
  };
}
